import { rgbToCSS } from "common/utilities/colors/colors";
import theme from "core/utilities/theme";
import type { SequenceAlign, SequenceType } from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import type { ColumnsLetterOccurrences } from "sequenceAnalyser/utilities/colorCalculations/colorCalculations";
import { ColorSchemesName, getColorSchemes } from "sequenceAnalyser/utilities/colors";

export interface ComponentProps {
  sequenceType: SequenceType;
  alignedSequences: SequenceAlign[];
  columnsLetterOccurrences: ColumnsLetterOccurrences;
  columnWidth: number;
  width: number;
  height: number;
}

function SequenceLogo({
  sequenceType,
  alignedSequences,
  columnsLetterOccurrences,
  columnWidth,
  width,
  height,
}: ComponentProps) {
  const heightSpacingPercentage = (1 * height) / 100; // 1px vertical spacing
  const scaleRatio = height / 10; // 1 (100%) to ... scaleRatio (max height)

  const colors =
    getColorSchemes(sequenceType)[sequenceType === "dna" ? ColorSchemesName.Nucleotide : ColorSchemesName.AminoAcid]
      ?.colors;

  return (
    <svg width={width} height={height} xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
      <g>
        {Object.keys(columnsLetterOccurrences).map((columnPositionStr) => {
          const columnPosition = Number(columnPositionStr);
          const letterOccurrences = columnsLetterOccurrences[columnPosition];
          const letters = Object.keys(letterOccurrences);
          const maxCount = Math.max(...letters.map((l) => letterOccurrences[l].count));
          const columnConservation = maxCount / alignedSequences.length;
          let lastYPosition = height;

          const sortedLetters = letters
            .filter((letter) => letter !== ".")
            .sort((a, b) => letterOccurrences[a].percentage - letterOccurrences[b].percentage);

          return sortedLetters.map((letter) => {
            const currentLastYPosition = lastYPosition;
            const occurrence = letterOccurrences[letter];
            const percentage = (occurrence.percentage / 100) * (columnConservation || 1);
            const scale = percentage * scaleRatio - heightSpacingPercentage / scaleRatio;
            lastYPosition -= percentage * height;

            return (
              <text
                key={columnPosition.toString() + letter}
                y={currentLastYPosition}
                x={columnPosition * columnWidth + 1}
                textLength={columnWidth - 2} // left/right spacing of 1px
                lengthAdjust="spacingAndGlyphs"
                transform={`scale(1, ${scale})`}
                fill={colors && colors[letter] ? rgbToCSS(colors[letter]) : undefined}
                style={{
                  // scale make the Y position changes, the following CSS helps to remain on the same position:
                  transformOrigin: "0 80%",
                  transformBox: "fill-box",
                  userSelect: "none",
                  fontFamily: theme.typography.fontFamily,
                  fontSize: "14px",
                }}
              >
                {letter}
                <title>{`Position: ${columnPosition + 1}`}</title>
              </text>
            );
          });
        })}
      </g>
    </svg>
  );
}

export default SequenceLogo;
